/*
 * Parses a WebVTT track into timed lines.
 *
 * This is what the Interactive Text panel is built from. `VideoDetail.segments`
 * is hardcoded to [] in videoService and always has been, but the backend does
 * ship a real caption track (see docs/captions-contract.md) — and the design's
 * timestamped transcript is exactly a cue list.
 */

export type VttCue = {
  id: string;
  /** Seconds. */
  start: number;
  end: number;
  text: string;
};

/**
 * `HH:MM:SS.mmm` or `MM:SS.mmm` to seconds. Returns null for anything else, so
 * a malformed line is skipped rather than poisoning the timeline with NaN.
 */
export function parseTimestamp(value: string): number | null {
  const match = value.trim().match(/^(?:(\d+):)?(\d{1,2}):(\d{2})(?:[.,](\d{1,3}))?$/);
  if (!match) return null;

  const [, hours, minutes, seconds, millis] = match;
  const total =
    Number(hours ?? 0) * 3600 +
    Number(minutes) * 60 +
    Number(seconds) +
    Number((millis ?? '0').padEnd(3, '0')) / 1000;

  return Number.isFinite(total) ? total : null;
}

/* `0:07`, the form the design uses beside each transcript line and each workpad
   note. Re-exported rather than defined here: it is the same clock the duration
   badges use, and the two drifted while they were separate. */
export { formatTimecode as formatCueTime } from '@/shared/utils/format';

const TIMING_SEPARATOR = '-->';

/* Cue payloads may carry inline markup (<v Speaker>, <b>, <c.classname>) and
   entity escapes. The transcript renders as text, so both are unwrapped. */
function stripCueMarkup(text: string): string {
  return text
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .trim();
}

export function parseVtt(vtt: string): VttCue[] {
  if (!vtt) return [];

  // Normalise line endings first — a CRLF file otherwise leaves \r on every
  // timestamp and the regex above rejects the lot.
  const lines = vtt.replace(/\r\n?/g, '\n').split('\n');
  const cues: VttCue[] = [];

  let index = 0;
  while (index < lines.length) {
    const line = lines[index];

    if (!line.includes(TIMING_SEPARATOR)) {
      index += 1;
      continue;
    }

    const [rawStart, rawRest] = line.split(TIMING_SEPARATOR);
    // Cue settings (align, line, position) trail the end time on the same line.
    const rawEnd = rawRest?.trim().split(/\s+/)[0] ?? '';

    const start = parseTimestamp(rawStart ?? '');
    const end = parseTimestamp(rawEnd);

    index += 1;
    if (start === null || end === null) continue;

    const payload: string[] = [];
    while (index < lines.length && lines[index].trim() !== '') {
      payload.push(lines[index]);
      index += 1;
    }

    const text = stripCueMarkup(payload.join(' '));
    if (text) {
      cues.push({ id: `cue-${cues.length}`, start, end, text });
    }
  }

  return cues;
}

/** The cue covering `time`, or null in a gap between cues. */
export function activeCueAt(cues: VttCue[], time: number): VttCue | null {
  return cues.find((cue) => time >= cue.start && time < cue.end) ?? null;
}
